/* Created by Interactive 63 - https://interactive63.com */
/**
 * A numeric field with a unit suffix. Supports typed maths, Up and Down stepping, scrubbing by
 * dragging the label, clamping to a range, and a "Mixed" state for differing values.
 */
import { evaluateMaths } from './maths-input';
import { h } from '../../../ui/dom';

export interface NumberInputOptions {
  label?: string;
  /** Accessible name when the visible label is not unique. Defaults to the label. */
  ariaLabel?: string;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Called with a new value. A coalesce key is given while scrubbing or stepping. */
  onChange: (value: number, coalesceKey?: string) => void;
  /** Key used to coalesce scrubs into one undo step. */
  scrubKey?: string;
}

/** Counts the decimals in a step so displayed values do not show float noise. */
function decimalsFor(step: number): number {
  const text = String(step);
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

export class NumberInput {
  readonly el: HTMLElement;
  readonly input: HTMLInputElement;
  private current: number | null = null;
  private readonly step: number;
  private readonly decimals: number;
  private readOnly = false;

  /** Builds the field. The label, when given, is the scrub handle. */
  constructor(private readonly options: NumberInputOptions) {
    this.step = options.step ?? 1;
    this.decimals = Math.max(decimalsFor(this.step), 2);
    this.input = h('input', {
      type: 'text',
      inputmode: 'decimal',
      class: 'number-input-field',
      autocomplete: 'off',
      'aria-label': options.ariaLabel ?? options.label ?? 'Value',
    });
    const label = options.label
      ? h('span', { class: 'number-input-label', title: 'Drag to change' }, options.label)
      : null;
    this.el = h(
      'span',
      { class: 'number-input' },
      label,
      this.input,
      options.unit ? h('span', { class: 'number-input-unit' }, options.unit) : null,
    );
    if (label) this.bindScrub(label);
    this.bindInput();
  }

  /** Shows a value, or "Mixed" for null. Does nothing while the field is being edited. */
  setValue(value: number | null, force = false): void {
    this.current = value;
    if (!force && document.activeElement === this.input) return;
    this.input.value = value === null ? '' : this.format(value);
    this.input.placeholder = value === null ? 'Mixed' : '';
  }

  /** Makes the field read-only, for example during playback. */
  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
    this.input.readOnly = readOnly;
    this.el.classList.toggle('is-readonly', readOnly);
  }

  /** Formats a value for display. */
  private format(value: number): string {
    return String(Number(value.toFixed(this.decimals)));
  }

  /** Limits a value to the configured range. */
  private clamp(value: number): number {
    return Math.min(this.options.max ?? Infinity, Math.max(this.options.min ?? -Infinity, value));
  }

  /** Applies a new value and shows its clamped form. */
  private apply(value: number, coalesceKey?: string): void {
    const clamped = this.clamp(value);
    this.current = clamped;
    this.input.value = this.format(clamped);
    this.options.onChange(clamped, coalesceKey);
  }

  /** Commits the typed text, reverting when it cannot be understood. */
  private commit(): void {
    const parsed = evaluateMaths(this.input.value);
    if (this.input.value.trim() === '' || parsed === null) {
      this.revert();
      return;
    }
    if (this.current !== null && Math.abs(parsed - this.current) < 1e-9) {
      this.revert();
      return;
    }
    this.apply(parsed);
  }

  /** Restores the last known value. */
  private revert(): void {
    this.input.value = this.current === null ? '' : this.format(this.current);
  }

  /** Wires focus, Enter, Escape, blur and arrow-key stepping. */
  private bindInput(): void {
    this.input.addEventListener('focus', () => {
      this.input.select();
    });
    this.input.addEventListener('blur', () => {
      if (!this.readOnly) this.commit();
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.commit();
        this.input.select();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.revert();
        this.input.blur();
      } else if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !this.readOnly) {
        event.preventDefault();
        const direction = event.key === 'ArrowUp' ? 1 : -1;
        const parsed = evaluateMaths(this.input.value);
        const base = parsed ?? this.current ?? 0;
        this.apply(base + direction * this.step * this.multiplier(event), this.options.scrubKey);
        this.input.select();
      }
    });
  }

  /** Step multiplier from modifier keys: Shift is 10 times, Alt is a tenth. */
  private multiplier(event: { shiftKey: boolean; altKey: boolean }): number {
    if (event.shiftKey) return 10;
    return event.altKey ? 0.1 : 1;
  }

  /** Lets the label be dragged to change the value, one step per two pixels. */
  private bindScrub(label: HTMLElement): void {
    label.addEventListener('pointerdown', (down) => {
      if (this.readOnly || down.button !== 0) return;
      down.preventDefault();
      label.setPointerCapture(down.pointerId);
      const startX = down.clientX;
      const startValue = this.current ?? 0;

      /** Updates the value from the drag distance. */
      const move = (event: PointerEvent): void => {
        const steps = Math.round((event.clientX - startX) / 2);
        this.apply(startValue + steps * this.step * this.multiplier(event), this.options.scrubKey);
      };
      /** Ends the drag. */
      const end = (): void => {
        label.removeEventListener('pointermove', move);
        label.removeEventListener('pointerup', end);
        label.removeEventListener('pointercancel', end);
      };
      label.addEventListener('pointermove', move);
      label.addEventListener('pointerup', end);
      label.addEventListener('pointercancel', end);
    });
  }
}
